import React, { useMemo, useRef, useEffect } from 'react';
import AntBaseForm from '../AntBaseForm';
import AntConfig from '../../utils/AntConfig';
import { AntGlobal } from '../../utils/AntContext';

var DATE_TYPES = ['date', 'datetime', 'unix_timestamp'];

// Tab "Thông tin chung" — render bằng AntBaseForm với config dataField của
// target_table (EXPOrder/APOrder/AROrder...). Lý do:
//   • AntBaseForm có sẵn editor đúng kiểu cho từng dataType: date picker, number
//     box (format số có locale), antDropDownGrid/antSelectBox cho lookup, mask
//     phone, currency editor… → không phải tự render text input universal.
//   • Label + editor cùng hàng (labelLocation='left' của DevExtreme Form) +
//     colCount=1 = 1 cột → khớp yêu cầu UX.
//   • Cấu hình field đã load sẵn trong appGlobal.tableConfigs[table] qua
//     init-fields.js → không phải refetch.
//
// Bridge với state header parent:
//   header[field] = { raw, resolved, status, editable, fk_table, ... }
//
//   • formData (in)  = { field: raw } cho mọi field có AI extract
//   • onFieldDataChanged (out) → patch info.raw lên parent header state →
//     Stage3TabbedReview debounce revalidate qua BE → re-coerce kiểu.
//
// Hạn chế: status icon (✓/⚠/ⓘ/✗) của HeaderDiffTable cũ KHÔNG hiện trong form
// này — DevExtreme Form không có slot tự nhiên cho icon sau editor. FE đã hiển
// thị validation messages ở phần cảnh báo trên/dưới form (Stage3TabbedReview)
// và FK resolution status sẽ phản ánh sau revalidate.

function hasExtractedValue(v) {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'boolean') return v === true;
    return true;
}

export default function HeaderForm(props) {
    var header = props.header || {};
    var onChange = props.onChange;
    var table = props.table;
    var appGlobal = AntGlobal();

    var formRef = useRef(null);

    // Truy cập config field để biết dataType (date/datetime/float/...) cho từng
    // field — quyết định lấy raw (string) hay resolved (đã coerce) làm value
    // hiển thị, và emit kiểu data đúng khi user edit.
    var cnf = useMemo(function () {
        if (!table) return null;
        var found = appGlobal && appGlobal.tableConfigs && appGlobal.tableConfigs.find
            ? appGlobal.tableConfigs.find(function (t) { return t.tableName === table; })
            : null;
        if (found) return found.config;
        var c = new AntConfig(appGlobal, table);
        if (!(appGlobal.tableConfigs instanceof Array)) appGlobal.tableConfigs = [];
        appGlobal.tableConfigs.push({ tableName: table, config: c });
        return c;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table]);

    function fieldDataType(field) {
        if (!cnf || !cnf.getCnfField) return 'string';
        var f = cnf.getCnfField(field);
        return (f && f.dataType) || 'string';
    }

    // Lấy giá trị hiển thị cho 1 field theo dataType:
    //  - date/datetime/unix_timestamp: ưu tiên resolved (unix int do BE coerce);
    //    fallback raw nếu BE chưa parse được.
    //  - khác: ưu tiên raw (user-edit-friendly); fallback resolved.
    function pickValue(field, info) {
        if (!info) return null;
        var dt = fieldDataType(field);
        if (DATE_TYPES.indexOf(dt) >= 0) {
            if (info.resolved != null && info.resolved !== '') return info.resolved;
            return info.raw;
        }
        if (info.raw != null && info.raw !== '') return info.raw;
        return info.resolved;
    }

    // Items: chỉ field có AI extract (raw có giá trị). Mỗi item = { dataField,
    // disable: false } để AntBaseForm tự pull config (label, dataType, editorType,
    // lookup) từ appGlobal.tableConfigs[table] NHƯNG bypass mọi rule disable của
    // BE configEdit (vd: field chỉ edit được khi status='New'). Ở giai đoạn AI
    // review, voucher CHƯA tồn tại trong DB → user phải edit được mọi field AI
    // bóc tách để chỉnh trước khi tạo phiếu. `disable: false` được isDisableField
    // ưu tiên cao nhất (xem AntBaseForm.js line 161).
    var items = useMemo(function () {
        return Object.keys(header)
            .filter(function (k) {
                var info = header[k];
                if (!info) return false;
                return hasExtractedValue(info.raw) || hasExtractedValue(info.resolved);
            })
            .map(function (k) { return { dataField: k, disable: false }; });
    }, [header]);

    // formData khởi tạo từ header[k] qua pickValue (dataType-aware).
    // AntBaseForm hold formData làm internal state; mọi edit của user reflect
    // qua onFieldDataChanged về parent.
    var initialFormData = useMemo(function () {
        var d = {};
        Object.keys(header).forEach(function (k) {
            var v = pickValue(k, header[k]);
            if (v != null) d[k] = v;
        });
        return d;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Khi parent header thay đổi (sau revalidate) → push values mới xuống form.
    useEffect(function () {
        if (!formRef.current || typeof formRef.current.refreshData !== 'function') return;
        var patch = {};
        Object.keys(header).forEach(function (k) {
            var v = pickValue(k, header[k]);
            if (v != null) patch[k] = v;
        });
        formRef.current.refreshData(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [header]);

    var onFieldDataChanged = function (changed) {
        if (!onChange || !changed) return;
        var next = Object.assign({}, header);
        Object.keys(changed).forEach(function (field) {
            var info = next[field] || {};
            var dt = fieldDataType(field);
            var v = changed[field];
            // Date editor emit unix (number) — đồng bộ luôn vào resolved nữa để
            // pickValue tiếp tục lấy đúng giá trị mới khi re-render.
            if (DATE_TYPES.indexOf(dt) >= 0 && typeof v === 'number') {
                next[field] = Object.assign({}, info, { raw: v, resolved: v });
            } else {
                next[field] = Object.assign({}, info, { raw: v });
            }
        });
        onChange(next);
    };

    if (items.length === 0) {
        return (
            <div className="ant-aiva-empty">
                {/* AntBaseForm cần items > 0 */}
            </div>
        );
    }

    return (
        <div className="s3-info-form">
            <AntBaseForm
                ref={formRef}
                table={table}
                type="Update"
                colCount={1}
                labelLocation="left"
                items={items}
                formData={initialFormData}
                onFieldDataChanged={onFieldDataChanged}
            />
        </div>
    );
}
